import React, { useEffect, useState } from 'react';
import '../styles/Login.css';
import '../styles/Spinner.css';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { useAuthAPI } from '../services/auth_api/AuthAPIHook';
import { authAPI } from '../services/auth_api/SessionAuthAPI';
import { errorUserNotFound } from '../services/auth_api/Errors';

const LoginUsername: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setSessionData, setRedirectUri, redirectUri } = useSession();
  const { submitUsername } = useAuthAPI(authAPI);
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [authRequestID, setAuthRequestID] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false); // New loading state

  useEffect(() => {
    // Fetch authRequestID from URL query params
    const urlParams = new URLSearchParams(window.location.search);
    console.log("URL Params:", urlParams);
    const authRequestIDParam = urlParams.get('authRequest');
    const redirectURI = urlParams.get('redirect_uri');
    console.log("authRequestIDParam:", authRequestIDParam);
    console.log("redirectURI:", redirectURI);

    if (redirectURI) {
      setRedirectUri(redirectURI);
    }
    
    if (authRequestIDParam) {
      // Base64 encode the authRequestID
      const encodedAuthRequestID = btoa(authRequestIDParam);
      setAuthRequestID(encodedAuthRequestID);
      // Set the redirect URI
    } else {
      console.log("Navigating to error with parms authRequestIDParam:", redirectURI);
      navigate('/login/error', {
        state: {
          errorMessage: t('error_generic_bad_request'),
          error: 'bad_request',
        }
      });
    }
  }, [navigate, t, setRedirectUri]);


  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError(null);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogin = async () => {
    if (!validateEmail(email)) {
      setError(t('error_invalid_email'));
      return;
    }
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    try {
      const res = await submitUsername(email, authRequestID!!);
      setSessionData(res);
      navigate('/login/password');
    }
    catch (err) {
      if (err === errorUserNotFound) {
        setError(t('error_user_not_found'));
        return;
      }
      console.error("Error submitting username:", err);
      navigate('/login/error', {
        state: {
          errorMessage: t('error_unexpected'),
          error: 'internal_error',
        }
      });
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="logo"></div>
      <h2 className="login-title">{t('ui_login')}</h2>
      <div className={`login-box ${error ? 'error' : ''}`}>
        <label htmlFor="email">{t('ui_enter_email')}</label>
        <div className="input-container">
          <input
            type="email"
            id="email"
            value={email}
            onChange={handleEmailChange}
            className={error ? 'error' : ''}
            placeholder={t('ui_email')}
          />
          {error && <span className="error-message">{error}</span>}
        </div>
        {/* Show the loading spinner when submitting, hide buttons */}
        {loading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <button
            onClick={handleLogin}
            disabled={!validateEmail(email)}
            className="submit-button"
          >
            {t('ui_next')}
          </button>
        )}
      </div>
    </div>
  );
};

export default LoginUsername;
